import { DataStatus, isIdle } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as CreativesAPI from '../api/creativesAPI';
import type { CreativeItem } from './types';

interface State {
  authStatus: DataStatus;
  items: Record<string, CreativeItem>;
  itemsByAdName: Record<string, CreativeItem>;
}

// Structural slice-state type used in condition callbacks to avoid circular RootState dependency.
interface StateShape {
  creatives?: State;
}

const initialState: State = {
  authStatus: DataStatus.idle,
  items: {},
  itemsByAdName: {},
};

export const creativesSlice = createAppSlice({
  name: 'creatives',
  initialState,
  reducers: (create) => ({
    initCreativesAuth: create.asyncThunk(
      (_: void) => CreativesAPI.authCreatives(),
      {
        pending: (state) => {
          state.authStatus = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.authStatus = DataStatus.finished;
        },
        rejected: (state) => {
          state.authStatus = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as StateShape;
            return isIdle(state.creatives?.authStatus ?? DataStatus.idle);
          },
        },
      },
    ),
    fetchCreatives: create.asyncThunk(
      (adIds: string[]) => CreativesAPI.searchCreatives(adIds),
      {
        fulfilled: (state, action) => {
          for (const item of action.payload) {
            state.items[item.adId] = item;
            state.itemsByAdName[item.adName] = item;
          }
        },
        options: {
          condition: (adIds: string[], { getState }) => {
            const state = getState() as StateShape;
            const newIds = adIds.filter((id) => !state.creatives?.items[id]);
            return newIds.length > 0;
          },
        },
      },
    ),
    fetchCreativesByAdName: create.asyncThunk(
      (adNames: string[]) => CreativesAPI.searchCreativesByAdName(adNames),
      {
        fulfilled: (state, action) => {
          for (const item of action.payload) {
            state.items[item.adId] = item;
            state.itemsByAdName[item.adName] = item;
          }
        },
        options: {
          condition: (adNames: string[], { getState }) => {
            const state = getState() as StateShape;
            const newNames = adNames.filter(
              (name) => !state.creatives?.itemsByAdName[name],
            );
            return newNames.length > 0;
          },
        },
      },
    ),
  }),
  selectors: {
    selectCreativesAuthStatus: (state) => state.authStatus,
    selectCreativesMap: (state) => state.items,
  },
});

export const { initCreativesAuth, fetchCreatives, fetchCreativesByAdName } =
  creativesSlice.actions;
export const { selectCreativesAuthStatus, selectCreativesMap } =
  creativesSlice.selectors;

export const selectCreativeByAdId =
  (adId: string) =>
  (state: StateShape): CreativeItem | undefined =>
    state.creatives?.items[adId];

export const selectCreativeByAdName =
  (adName: string) =>
  (state: StateShape): CreativeItem | undefined =>
    state.creatives?.itemsByAdName[adName];
